/**
 * ChartSelectionModal.jsx
 * Modal that lets users select which charts to include in DOCX/PDF exports.
 * Shows available charts grouped by section with recommended charts pre-checked.
 * Hover over a chart name to see a live miniature preview.
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { X, Check, RotateCcw, Eye } from 'lucide-react';
import { SECTION_TAB_MAP } from './charts/chartRegistry';
import { PartnershipDataProvider } from './charts/partnership/usePartnershipData.jsx';
import { PortfolioDataProvider } from './charts/portfolio/usePortfolioData';
import { useApp } from '../context/AppContext';

const SECTION_LABELS = {
  overview: 'Section 1: Overview',
  progress: 'Section 2: Progress along ToC',
  partnerships: 'Section 3: Partnerships',
  innovation: 'Innovation Analytics',
};

const PORTFOLIO_SECTION_LABELS = {
  'portfolio-overview': '1. Portfolio Overview',
  'portfolio-impact-areas': '2. Impact Area Focus',
  'portfolio-regional': '3. Regional Focus',
  'portfolio-partnerships': '4. Partnerships',
};

const CHART_OPTIONS = {
  overview: [
    { key: 'overview', label: 'Results Composition', recommended: true },
    { key: 'kpi', label: 'Key Metrics', recommended: true },
    { key: 'sankey', label: 'Pathway Flow', recommended: true },
    { key: 'targets', label: 'Progress Against Targets', recommended: true },
    { key: 'tocOverview', label: 'ToC Overview' },
    { key: 'treemap', label: 'Result Treemap' },
    { key: 'toc', label: 'ToC Sunburst' },
    { key: 'impact', label: 'Impact Areas' },
  ],
  progress: [
    { key: 'map', label: 'Results Map', recommended: true },
    { key: 'byTypeFunding', label: 'Type & Funding' },
    { key: 'impact', label: 'Impact Areas' },
    { key: 'countries', label: 'Top Countries' },
    { key: 'regions', label: 'Regions' },
    { key: 'byLevel', label: 'Output vs Outcome' },
    { key: 'innovation', label: 'Innovation Pipeline' },
    { key: 'spheres', label: 'Concentric Spheres' },
    { key: 'petals', label: 'Donut & Petals' },
    { key: 'aowFramework', label: 'ToC Framework (per AoW)', recommended: true },
    { key: 'aowFrameworkDetail', label: 'ToC Detail (per AoW)' },
    { key: 'aowTrajectory', label: 'AoW Trajectory (per AoW)' },
  ],
  partnerships: [
    { key: 'partnerFlow', label: 'Partner Flow', recommended: true },
    { key: 'partnerMap', label: 'Partner Map', recommended: true },
    { key: 'linkages', label: 'Portfolio Linkages' },
    { key: 'landscape', label: 'Landscape' },
    { key: 'topPartners', label: 'Top Partners' },
    { key: 'coverage', label: 'Coverage' },
    { key: 'network', label: 'Partner Network' },
    { key: 'radial', label: 'Radial View' },
    { key: 'centers', label: 'Centers' },
    { key: 'w3Projects', label: 'W3/Bilateral Projects' },
    { key: 'funders', label: 'Funders' },
    { key: 'crossProgram', label: 'Cross-Program' },
  ],
  innovation: [
    { key: 'type', label: 'Results by Type', recommended: true },
    { key: 'useLevel', label: 'Use Level', recommended: true },
    { key: 'readiness', label: 'Readiness Level', recommended: true },
    { key: 'geography', label: 'Geography', recommended: true },
    { key: 'crossProgram', label: 'Cross-Program', recommended: true },
    { key: 'nature', label: 'Nature & Typology' },
    { key: 'evolution', label: 'Evolution' },
    { key: 'linkages', label: 'Linkages' },
    { key: 'packages', label: 'Packages' },
    { key: 'users', label: 'Users & Reach' },
    { key: 'discontinued', label: 'Discontinued' },
    { key: 'impact', label: 'Impact Areas' },
    { key: 'partners', label: 'Partners' },
    { key: 'collaboration', label: 'Collaboration' },
  ],
};

const PORTFOLIO_CHART_OPTIONS = {
  'portfolio-overview': [
    { key: 'programBreakdown', label: 'Results by Program', recommended: true },
    { key: 'kpi', label: 'Portfolio KPIs', recommended: true },
    { key: 'impactAreas', label: 'Impact Area Distribution', recommended: true },
    { key: 'crossProgram', label: 'Cross-Program Heatmap' },
  ],
  'portfolio-impact-areas': [
    { key: 'iaGender', label: 'Level Distribution', recommended: true, subsection: 'Gender Equality' },
    { key: 'iaGenderGeoMap', label: 'Geographic Map', subsection: 'Gender Equality' },
    { key: 'iaGenderProgram', label: 'By Program', subsection: 'Gender Equality' },
    { key: 'iaGenderByType', label: 'By Result Type', subsection: 'Gender Equality' },
    { key: 'iaGenderFunding', label: 'Funding Split', subsection: 'Gender Equality' },
    { key: 'iaGenderChord', label: 'Cross-Program Chord', subsection: 'Gender Equality' },
    { key: 'iaGenderHeatmap', label: 'Cross-Program Heatmap', subsection: 'Gender Equality' },
    { key: 'iaGenderSynergies', label: 'IA Synergies', subsection: 'Gender Equality' },
    { key: 'iaClimate', label: 'Level Distribution', recommended: true, subsection: 'Climate Adaptation' },
    { key: 'iaClimateGeoMap', label: 'Geographic Map', subsection: 'Climate Adaptation' },
    { key: 'iaClimateProgram', label: 'By Program', subsection: 'Climate Adaptation' },
    { key: 'iaClimateByType', label: 'By Result Type', subsection: 'Climate Adaptation' },
    { key: 'iaClimateFunding', label: 'Funding Split', subsection: 'Climate Adaptation' },
    { key: 'iaClimateChord', label: 'Cross-Program Chord', subsection: 'Climate Adaptation' },
    { key: 'iaClimateHeatmap', label: 'Cross-Program Heatmap', subsection: 'Climate Adaptation' },
    { key: 'iaClimateSynergies', label: 'IA Synergies', subsection: 'Climate Adaptation' },
    { key: 'iaNutrition', label: 'Level Distribution', recommended: true, subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaNutritionGeoMap', label: 'Geographic Map', subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaNutritionProgram', label: 'By Program', subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaNutritionByType', label: 'By Result Type', subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaNutritionFunding', label: 'Funding Split', subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaNutritionChord', label: 'Cross-Program Chord', subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaNutritionHeatmap', label: 'Cross-Program Heatmap', subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaNutritionSynergies', label: 'IA Synergies', subsection: 'Nutrition, Health & Food Security' },
    { key: 'iaBiodiversity', label: 'Level Distribution', recommended: true, subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaBiodiversityGeoMap', label: 'Geographic Map', subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaBiodiversityProgram', label: 'By Program', subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaBiodiversityByType', label: 'By Result Type', subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaBiodiversityFunding', label: 'Funding Split', subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaBiodiversityChord', label: 'Cross-Program Chord', subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaBiodiversityHeatmap', label: 'Cross-Program Heatmap', subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaBiodiversitySynergies', label: 'IA Synergies', subsection: 'Environmental Health & Biodiversity' },
    { key: 'iaPoverty', label: 'Level Distribution', recommended: true, subsection: 'Poverty Reduction' },
    { key: 'iaPovertyGeoMap', label: 'Geographic Map', subsection: 'Poverty Reduction' },
    { key: 'iaPovertyProgram', label: 'By Program', subsection: 'Poverty Reduction' },
    { key: 'iaPovertyByType', label: 'By Result Type', subsection: 'Poverty Reduction' },
    { key: 'iaPovertyFunding', label: 'Funding Split', subsection: 'Poverty Reduction' },
    { key: 'iaPovertyChord', label: 'Cross-Program Chord', subsection: 'Poverty Reduction' },
    { key: 'iaPovertyHeatmap', label: 'Cross-Program Heatmap', subsection: 'Poverty Reduction' },
    { key: 'iaPovertySynergies', label: 'IA Synergies', subsection: 'Poverty Reduction' },
  ],
  'portfolio-regional': [
    { key: 'africaBar', label: 'Top Countries', recommended: true, subsection: 'Africa' },
    { key: 'africaMap', label: 'Geographic Map', subsection: 'Africa' },
    { key: 'africaProgram', label: 'By Program', subsection: 'Africa' },
    { key: 'africaByType', label: 'By Result Type', subsection: 'Africa' },
    { key: 'africaFunding', label: 'Funding Split', subsection: 'Africa' },
    { key: 'africaChord', label: 'Cross-Program Chord', subsection: 'Africa' },
    { key: 'africaHeatmap', label: 'Cross-Program Heatmap', subsection: 'Africa' },
    { key: 'africaImpactArea', label: 'Impact Area Coverage', subsection: 'Africa' },
    { key: 'americasBar', label: 'Top Countries', recommended: true, subsection: 'The Americas' },
    { key: 'americasMap', label: 'Geographic Map', subsection: 'The Americas' },
    { key: 'americasProgram', label: 'By Program', subsection: 'The Americas' },
    { key: 'americasByType', label: 'By Result Type', subsection: 'The Americas' },
    { key: 'americasFunding', label: 'Funding Split', subsection: 'The Americas' },
    { key: 'americasChord', label: 'Cross-Program Chord', subsection: 'The Americas' },
    { key: 'americasHeatmap', label: 'Cross-Program Heatmap', subsection: 'The Americas' },
    { key: 'americasImpactArea', label: 'Impact Area Coverage', subsection: 'The Americas' },
    { key: 'asiaBar', label: 'Top Countries', recommended: true, subsection: 'Asia' },
    { key: 'asiaMap', label: 'Geographic Map', subsection: 'Asia' },
    { key: 'asiaProgram', label: 'By Program', subsection: 'Asia' },
    { key: 'asiaByType', label: 'By Result Type', subsection: 'Asia' },
    { key: 'asiaFunding', label: 'Funding Split', subsection: 'Asia' },
    { key: 'asiaChord', label: 'Cross-Program Chord', subsection: 'Asia' },
    { key: 'asiaHeatmap', label: 'Cross-Program Heatmap', subsection: 'Asia' },
    { key: 'asiaImpactArea', label: 'Impact Area Coverage', subsection: 'Asia' },
  ],
  'portfolio-partnerships': [
    { key: 'partnerFlow', label: 'Partner Type Distribution', recommended: true },
    { key: 'topPartners', label: 'Top Partners', recommended: true },
    { key: 'partnerMap', label: 'Geographic Spread' },
    { key: 'crossProgram', label: 'Cross-Program Partnerships' },
  ],
};

function getDefaults(optionsMap) {
  const selections = {};
  for (const [section, options] of Object.entries(optionsMap)) {
    selections[section] = options.filter(o => o.recommended).map(o => o.key);
  }
  return selections;
}

/** Renders a live chart component in a scaled-down preview container. */
function ChartPreview({ section, chartKey }) {
  const tabs = SECTION_TAB_MAP[section];
  const Component = tabs?.[chartKey];
  if (!Component) return null;

  const chart = <Component />;
  const needsPartnership = section === 'partnerships' || section === 'portfolio-partnerships';
  const needsPortfolio = section.startsWith('portfolio-');
  let wrapped = chart;
  if (needsPartnership) wrapped = <PartnershipDataProvider>{wrapped}</PartnershipDataProvider>;
  if (needsPortfolio) wrapped = <PortfolioDataProvider>{wrapped}</PortfolioDataProvider>;

  return (
    <div
      className="bg-white rounded-lg shadow-2xl border border-[var(--border-primary)] overflow-hidden"
      style={{ maxWidth: 380, maxHeight: 300 }}
    >
      <style>{`.chart-preview-mini .border-t.mt-4 { display: none !important; }
.chart-preview-mini [class*="download-btn"] { display: none !important; }`}</style>
      <div
        className="chart-preview-mini"
        style={{
          width: 900,
          zoom: 0.4,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {wrapped}
      </div>
    </div>
  );
}

export default function ChartSelectionModal({ format, onConfirm, onCancel }) {
  const { reportMode } = useApp();
  const isPortfolio = reportMode === 'portfolio';
  const activeOptions = isPortfolio ? PORTFOLIO_CHART_OPTIONS : CHART_OPTIONS;
  const activeLabels = isPortfolio ? PORTFOLIO_SECTION_LABELS : SECTION_LABELS;

  const [selections, setSelections] = useState(() => getDefaults(activeOptions));
  const [preview, setPreview] = useState(null); // { section, key, x, y }
  const modalRef = useRef(null);

  const formatLabel = format === 'docx' ? 'DOCX' : 'PDF';

  const totalSelected = useMemo(
    () => Object.values(selections).reduce((sum, arr) => sum + arr.length, 0),
    [selections],
  );

  const toggle = (section, key) => {
    setSelections(prev => {
      const current = prev[section] || [];
      const next = current.includes(key)
        ? current.filter(k => k !== key)
        : [...current, key];
      return { ...prev, [section]: next };
    });
  };

  const toggleAll = (section) => {
    setSelections(prev => {
      const options = activeOptions[section];
      const allSelected = options.every(o => (prev[section] || []).includes(o.key));
      return {
        ...prev,
        [section]: allSelected ? [] : options.map(o => o.key),
      };
    });
  };

  const resetDefaults = () => setSelections(getDefaults(activeOptions));

  const handlePreviewEnter = useCallback((section, key, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPreview({ section, key, x: rect.left, y: rect.top });
  }, []);

  const handlePreviewLeave = useCallback(() => {
    setPreview(null);
  }, []);

  // Position preview to the left of the modal, or fallback to right
  const previewStyle = useMemo(() => {
    if (!preview || !modalRef.current) return {};
    const modalRect = modalRef.current.getBoundingClientRect();
    const previewW = 380;
    const previewH = 300;
    const gap = 12;

    // Try left of modal
    let left = modalRect.left - previewW - gap;
    if (left < 8) {
      // Fall back to right of modal
      left = modalRect.right + gap;
    }

    // Vertically: align with the hovered item, clamped to viewport
    let top = preview.y - previewH / 2 + 16;
    top = Math.max(8, Math.min(top, window.innerHeight - previewH - 8));

    return { position: 'fixed', left, top, zIndex: 110 };
  }, [preview]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="bg-[var(--surface-primary)] rounded-xl shadow-2xl border border-[var(--border-primary)] w-full max-w-lg mx-4 max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-primary)]">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-primary)]">Select Charts for Export</h2>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              {totalSelected} chart{totalSelected !== 1 ? 's' : ''} selected · hover to preview
            </p>
          </div>
          <button onClick={onCancel} className="p-1.5 hover:bg-[var(--surface-hover)] rounded-lg transition-colors">
            <X className="w-4 h-4 text-[var(--text-secondary)]" />
          </button>
        </div>

        {/* Chart list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {Object.entries(activeOptions).map(([section, options]) => (
            <div key={section}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-[var(--text-primary)]">{activeLabels[section]}</h3>
                <button
                  onClick={() => toggleAll(section)}
                  className="text-xs text-cgiar-green hover:text-cgiar-green-dark transition-colors"
                >
                  {options.every(o => (selections[section] || []).includes(o.key)) ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              {(() => {
                const hasSubsections = options.some(o => o.subsection);
                if (hasSubsections) {
                  // Group options by subsection, preserving order
                  const groups = [];
                  let currentGroup = null;
                  for (const option of options) {
                    if (!currentGroup || currentGroup.subsection !== option.subsection) {
                      currentGroup = { subsection: option.subsection, items: [] };
                      groups.push(currentGroup);
                    }
                    currentGroup.items.push(option);
                  }
                  return groups.map(group => (
                    <div key={group.subsection} className="mb-2 last:mb-0">
                      <div className="text-xs font-medium text-[var(--text-secondary)] mb-1 ml-0.5">{group.subsection}</div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {group.items.map(option => {
                          const checked = (selections[section] || []).includes(option.key);
                          const isHovered = preview?.section === section && preview?.key === option.key;
                          return (
                            <label
                              key={option.key}
                              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors text-sm ${
                                checked
                                  ? 'bg-cgiar-green-tint text-[var(--text-primary)]'
                                  : 'hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]'
                              } ${isHovered ? 'ring-1 ring-cgiar-green/40' : ''}`}
                              onMouseEnter={(e) => handlePreviewEnter(section, option.key, e)}
                              onMouseLeave={handlePreviewLeave}
                            >
                              <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                checked
                                  ? 'bg-cgiar-green border-cgiar-green'
                                  : 'border-[var(--border-primary)]'
                              }`}>
                                {checked && <Check className="w-3 h-3 text-white" />}
                              </span>
                              <span className="truncate flex-1">{option.label}</span>
                              {option.recommended && (
                                <span className="flex-shrink-0 text-[10px] text-cgiar-green font-medium">REC</span>
                              )}
                              <Eye className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0 opacity-0 group-hover:opacity-100" />
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={checked}
                                onChange={() => toggle(section, option.key)}
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ));
                }
                // Flat grid for sections without subsections
                return (
                  <div className="grid grid-cols-2 gap-1.5">
                    {options.map(option => {
                      const checked = (selections[section] || []).includes(option.key);
                      const isHovered = preview?.section === section && preview?.key === option.key;
                      return (
                        <label
                          key={option.key}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors text-sm ${
                            checked
                              ? 'bg-cgiar-green-tint text-[var(--text-primary)]'
                              : 'hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]'
                          } ${isHovered ? 'ring-1 ring-cgiar-green/40' : ''}`}
                          onMouseEnter={(e) => handlePreviewEnter(section, option.key, e)}
                          onMouseLeave={handlePreviewLeave}
                        >
                          <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                            checked
                              ? 'bg-cgiar-green border-cgiar-green'
                              : 'border-[var(--border-primary)]'
                          }`}>
                            {checked && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="truncate flex-1">{option.label}</span>
                          {option.recommended && (
                            <span className="flex-shrink-0 text-[10px] text-cgiar-green font-medium">REC</span>
                          )}
                          <Eye className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0 opacity-0 group-hover:opacity-100" />
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={checked}
                            onChange={() => toggle(section, option.key)}
                          />
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-primary)]">
          <button
            onClick={resetDefaults}
            className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to recommended
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3.5 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(selections)}
              disabled={totalSelected === 0}
              className="px-4 py-1.5 text-sm bg-cgiar-green text-white hover:bg-cgiar-green-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Export {formatLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Floating chart preview */}
      {preview && (
        <div style={previewStyle} className="pointer-events-none">
          <ChartPreview section={preview.section} chartKey={preview.key} />
        </div>
      )}
    </div>
  );
}
